import React, { useState, useEffect, useCallback } from 'react';
import '../styles/TimbraturaTablet.css';
import intestazioniTablet from '../utils/chiaveDispositivo';
import { oggiClinica, oraIta } from '../utils/oraItaliana';

/**
 * TimbraturaTablet — si tocca il proprio nome, si scrive il PIN, si timbra.
 *
 * DUE FATTORI, E NESSUNA SESSIONE
 * La chiave del dispositivo arriva da chi ci monta sopra (la pagina
 * /tablet, che la tiene in localStorage per la firma dei moduli); il PIN
 * lo scrive la persona. Ogni timbratura e' una richiesta a se': non
 * esiste un "sono entrata" che resta valido, e allontanarsi dal tablet
 * non lascia niente aperto.
 *
 * IL PIN NON RESTA A SCHERMO
 * Si mostrano pallini, non le cifre, e il campo si azzera a ogni esito -
 * riuscito o sbagliato. Un tablet in segreteria e' appoggiato su un banco
 * dove passano pazienti.
 *
 * NON DECIDE SE E' ENTRATA O USCITA
 * Lo mostra, perche' serve a sapere cosa sta per fare, ma la decisione la
 * prende il server dall'ultima timbratura valida. Se due tablet fossero
 * aperti sulla stessa persona, quello con lo schermo vecchio non puo'
 * registrare una seconda entrata.
 *
 * LA TIMBRATURA DIMENTICATA (Alex, 7/10/2026)
 * Dopo il nome, «Ho dimenticato di timbrare»: entrata o uscita, l'ora e il
 * motivo, poi il PIN. Solo la giornata di oggi e mai un'ora futura: lo
 * decide il server (e il database), qui si avvisa prima soltanto per non
 * far scrivere il PIN a vuoto. Non tocca nessuna timbratura esistente: ne
 * aggiunge una, con l'ora dichiarata e quella vera dell'inserimento.
 *
 * Props:
 *  - chiave     la chiave del dispositivo
 *  - onChiudi   torna alla schermata di attesa
 */

const TimbraturaTablet = ({ chiave, onChiudi }) => {
    const [dipendenti, setDipendenti] = useState([]);
    const [dispositivo, setDispositivo] = useState('');
    const [scelto, setScelto] = useState(null);
    const [pin, setPin] = useState('');
    const [errore, setErrore] = useState(null);
    const [esito, setEsito] = useState(null);
    const [invio, setInvio] = useState(false);
    const [caricamento, setCaricamento] = useState(true);
    /* null, oppure il modulo della timbratura dimenticata:
       { tipo, ora, motivo, pronto } - pronto vuol dire che manca il PIN. */
    const [dimentica, setDimentica] = useState(null);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await fetch('/api/firma-tablet/timbratura',
                                  { headers: intestazioniTablet(chiave) });
            const d = await r.json();
            if (!d.success) {
                setErrore(d.message || 'Timbratura non disponibile su questo dispositivo');
                setDipendenti([]);
                return;
            }
            setErrore(null);
            setDipendenti(d.data.dipendenti || []);
            setDispositivo(d.data.dispositivo || '');
        } catch (e) {
            setErrore('Non riesco a contattare il server');
        } finally {
            setCaricamento(false);
        }
    }, [chiave]);

    useEffect(() => { carica(); }, [carica]);

    /* L'esito resta a schermo pochi secondi e poi si torna all'elenco: un
       tablet condiviso non deve restare sul nome di chi ha appena timbrato,
       perche' la prossima persona tocca senza guardare. */
    useEffect(() => {
        if (!esito) return undefined;
        /* Con un avviso (la sequenza di oggi non torna) resta di piu': va
           letto. Il modulo della timbratura dimenticata si svuota insieme
           al resto: il tablet non tiene niente di chi ha appena timbrato. */
        const t = setTimeout(() => {
            setEsito(null);
            setScelto(null);
            setDimentica(null);
            carica();
        }, esito.data?.avviso ? 9000 : 4000);
        return () => clearTimeout(t);
    }, [esito, carica]);

    const premi = (cifra) => {
        setErrore(null);
        setPin((p) => (p.length >= 4 ? p : p + cifra));
    };

    const timbra = useCallback(async () => {
        if (pin.length !== 4 || !scelto) return;
        try {
            setInvio(true);
            setErrore(null);
            const dim = dimentica && dimentica.pronto ? dimentica : null;
            const r = await fetch(dim ? '/api/firma-tablet/timbratura/dimenticata'
                                      : '/api/firma-tablet/timbratura', {
                method: 'POST',
                headers: intestazioniTablet(chiave, true),
                body: JSON.stringify(dim
                    ? { dipendente_id: scelto.id, pin, tipo: dim.tipo, ora: dim.ora,
                        motivo: dim.motivo, giorno: oggiClinica() }
                    : { dipendente_id: scelto.id, pin }),
            });
            const d = await r.json();
            // Il PIN si azzera comunque: se era sbagliato non lo si
            // ripropone corretto di una cifra, si riscrive.
            setPin('');
            if (!d.success) {
                setErrore(d.message || 'Timbratura non registrata');
                /* Un 400 della timbratura dimenticata riguarda il modulo
                   (l'ora, il giorno, il motivo): si torna li' a correggerlo. */
                if (dim && r.status === 400) setDimentica({ ...dim, pronto: false });
                return;
            }
            setEsito(d);
        } catch (e) {
            setPin('');
            setErrore('Non riesco a contattare il server');
        } finally {
            setInvio(false);
        }
    }, [chiave, pin, scelto, dimentica]);

    // Quattro cifre: si invia da se', senza chiedere anche di confermare
    useEffect(() => {
        if (pin.length === 4 && !invio) timbra();
        /* eslint-disable-next-line */
    }, [pin]);

    /* ---------- esito ---------- */
    if (esito) {
        const t = esito.data.timbratura;
        const g = esito.data.giornata;
        return (
            <div className={`tmb-esito ${t.tipo}`}>
                <div className="tmb-segno">{t.tipo === 'entrata' ? '→' : '←'}</div>
                <h1>{esito.message}</h1>
                <p className="tmb-ora">
                    {new Date(t.momento).toLocaleTimeString('it-IT',
                        { hour: '2-digit', minute: '2-digit' })}
                </p>
                {t.dichiarata_il && (
                    <p className="tmb-totale">
                        Ora dichiarata, inserita alle {oraIta(t.dichiarata_il)}
                    </p>
                )}
                {esito.data.avviso && <p className="tmb-avviso">{esito.data.avviso}</p>}
                {g && parseFloat(g.ore) > 0 && (
                    <p className="tmb-totale">
                        Oggi: {parseFloat(g.ore).toLocaleString('it-IT',
                            { maximumFractionDigits: 2 })} ore
                    </p>
                )}
            </div>
        );
    }

    /* ---------- la timbratura dimenticata: il modulo ---------- */
    if (scelto && dimentica && !dimentica.pronto) {
        const adesso = oraIta(new Date());
        const futura = dimentica.ora && dimentica.ora > adesso;
        const valido = dimentica.tipo && /^\d{2}:\d{2}$/.test(dimentica.ora || '')
                     && !futura && dimentica.motivo.trim().length >= 3;
        return (
            <div className="tmb-wrap">
                <div className="tmb-testa">
                    <button className="tmb-indietro"
                            onClick={() => { setDimentica(null); setErrore(null); }}>
                        ‹ Indietro
                    </button>
                    <span>{dispositivo}</span>
                </div>
                <h2 className="tmb-nome">{scelto.nominativo}</h2>
                <p className="tmb-cosa">Ho dimenticato di timbrare, oggi</p>

                <div className="tmb-scelta" role="group" aria-label="Entrata o uscita">
                    {['entrata', 'uscita'].map((x) => (
                        <button key={x} type="button"
                                className={dimentica.tipo === x ? 'scelto' : ''}
                                aria-pressed={dimentica.tipo === x}
                                onClick={() => setDimentica({ ...dimentica, tipo: x })}>
                            {x === 'entrata' ? 'Entrata' : 'Uscita'}
                        </button>
                    ))}
                </div>
                <label className="tmb-campo">
                    <span>Alle ore</span>
                    <input type="time" value={dimentica.ora}
                           onChange={(e) => setDimentica({ ...dimentica, ora: e.target.value })} />
                </label>
                {futura && <p className="tmb-errore">L&apos;ora indicata non è ancora arrivata.</p>}
                <label className="tmb-campo">
                    <span>Motivo</span>
                    <textarea rows="2" value={dimentica.motivo} maxLength={300}
                              placeholder="Per esempio: ero al telefono con un paziente"
                              onChange={(e) => setDimentica({ ...dimentica, motivo: e.target.value })} />
                </label>
                <p className="tmb-nota">
                    Vale solo per oggi. La timbratura si aggiunge a quelle che ci sono,
                    con l&apos;ora che scrivi e l&apos;ora vera di adesso: l&apos;amministrazione le vede tutte e due.
                </p>
                {errore && <p className="tmb-errore">{errore}</p>}
                <button className="tmb-avanti" disabled={!valido}
                        onClick={() => { setErrore(null); setPin('');
                                         setDimentica({ ...dimentica, pronto: true }); }}>
                    Avanti: il PIN
                </button>
            </div>
        );
    }

    /* ---------- tastierino ---------- */
    if (scelto) {
        return (
            <div className="tmb-wrap">
                <div className="tmb-testa">
                    <button className="tmb-indietro"
                            onClick={() => {
                                /* Dal PIN della timbratura dimenticata si torna al
                                   suo modulo; dal PIN normale all'elenco. */
                                if (dimentica) { setDimentica({ ...dimentica, pronto: false }); }
                                else { setScelto(null); }
                                setPin(''); setErrore(null);
                            }}>
                        ‹ Indietro
                    </button>
                    <span>{dispositivo}</span>
                </div>

                <h2 className="tmb-nome">{scelto.nominativo}</h2>
                {/* Non si dice piu' se sara' entrata o uscita PRIMA del PIN:
                    quell'informazione, insieme all'ora dell'ultima
                    timbratura, diceva a chiunque tenesse il tablet chi era
                    gia' in servizio e da quando. Il tipo lo decide il
                    server dall'ultima timbratura valida, e lo si legge nel
                    messaggio di conferma. */}
                <p className="tmb-cosa">
                    {dimentica
                        ? `Inserisci il tuo PIN per registrare l'${dimentica.tipo} dimenticata delle ${dimentica.ora}`
                        : 'Inserisci il tuo PIN'}
                </p>

                <div className="tmb-pallini" aria-label={`${pin.length} cifre su 4`}>
                    {[0, 1, 2, 3].map((i) => (
                        <span key={i} className={i < pin.length ? 'pieno' : ''} />
                    ))}
                </div>

                {errore && <p className="tmb-errore">{errore}</p>}
                {invio && <p className="tmb-attesa">Registro…</p>}

                <div className="tmb-tastierino">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <button key={n} onClick={() => premi(String(n))} disabled={invio}>{n}</button>
                    ))}
                    <button className="tmb-vuoto" disabled />
                    <button onClick={() => premi('0')} disabled={invio}>0</button>
                    <button className="tmb-canc" onClick={() => setPin((p) => p.slice(0, -1))}
                            disabled={invio || !pin.length}>⌫</button>
                </div>
                {!dimentica && (
                    <button className="tmb-dimenticata" disabled={invio}
                            onClick={() => {
                                setPin(''); setErrore(null);
                                setDimentica({ tipo: '', ora: '', motivo: '', pronto: false });
                            }}>
                        Ho dimenticato di timbrare
                    </button>
                )}
            </div>
        );
    }

    /* ---------- elenco ---------- */
    return (
        <div className="tmb-wrap">
            <div className="tmb-testa">
                <button className="tmb-indietro" onClick={onChiudi}>‹ Indietro</button>
                <span>{dispositivo}</span>
            </div>

            <h2 className="tmb-titolo">Chi sta timbrando?</h2>

            {errore && <p className="tmb-errore">{errore}</p>}

            {caricamento ? (
                <p className="tmb-attesa">Caricamento…</p>
            ) : errore ? (
                /* Con un errore l'elenco e' vuoto perche' la richiesta si e'
                   fermata prima, non perche' non ci sia nessuno. Dirlo
                   comunque mandava a cercare il problema nel posto
                   sbagliato: e' successo davvero. */
                null
            ) : dipendenti.length === 0 ? (
                <p className="tmb-attesa">
                    Nessun dipendente in organico. Va aggiunto dall&apos;area
                    dipendenti del gestionale.
                </p>
            ) : (
                <div className="tmb-elenco">
                    {dipendenti.map((d) => (
                        /* SOLO IL NOME. Prima accanto a ognuno c'erano lo
                           stato del PIN, l'ora dell'ultima timbratura e le
                           ore di oggi: su un tablet che durante la
                           compilazione sta in mano a un paziente, era
                           l'elenco di chi e' in servizio e di chi non ha
                           ancora impostato il PIN. Chi non ha il PIN lo
                           scopre provando, con un messaggio che lo dice
                           solo a lui. */
                        <button key={d.id} className="tmb-persona"
                                onClick={() => { setScelto(d); setPin(''); setErrore(null); setDimentica(null); }}>
                            <span className="tmb-persona-nome">{d.nominativo}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TimbraturaTablet;
